import {Strings} from '@/assets';
import VisitorParser from './VisitorParser';
import queries from '@/query/queries';
import {redirect as routerRedirect} from 'react-router-dom';
import MetaParser from './MetaParser';

interface Data {
  visitor_id: string;
  visa_request_id: string;
}

class RouteParser extends VisitorParser {
  private url: URL;
  private visa_request_id: string;
  private request: VisaRequest;
  private urlRequest: Request;
  private meta: MetaParser;

  constructor(request: Request, data: Data, meta: MetaParser) {
    const visitor = queries.visitor(data.visa_request_id);
    super(visitor);
    this.urlRequest = request;
    this.visa_request_id = data.visa_request_id;
    this.url = new URL(request.url);
    this.request = queries.request(data.visa_request_id);
    this.meta = meta;
  }

  public redirection = async () => {
    if (this.meta.payment_required) {
      switch (this.request.status) {
        case 'completed':
        case 'submitted':
        case 'background_process_completed':
        case 'submitting':
          return this.redirect(Strings.ROUTES.SUCCESS);
        case 'paid':
          return this.handleRouting();
        case 'draft':
          return this.redirect(Strings.ROUTES.PAYMENT);
        case 'expired':
        case 'purged':
          return this.redirect(Strings.ROUTES.EXPIRED);
        default:
          return this.redirect(Strings.ROUTES.ADDITIONAL);
      }
    } else {
      switch (this.request.status) {
        case 'completed':
        case 'submitted':
        case 'background_process_completed':
        case 'submitting':
          return this.redirect(Strings.ROUTES.SUCCESS);
        case 'expired':
        case 'purged':
          return this.redirect(Strings.ROUTES.EXPIRED);
        default:
          return this.request;
      }
    }
  };

  handleRouting() {
    if (this.ignore) {
      return this.request;
    } else if (this.goto.passport) {
      return this.redirect(Strings.ROUTES.PASSPORT);
    } else if (this.goto.visa) {
      return this.redirect(Strings.ROUTES.VISA);
    } else if (this.goto.financial) {
      return this.redirect(Strings.ROUTES.FINANCIAL);
    } else if (this.goto.additional) {
      return this.redirect(Strings.ROUTES.ADDITIONAL);
    } else {
      return this.redirect(this.meta.steps[0].route);
    }
  }

  get capture_message() {
    return this.meta.country
      ? this.meta.country.toUpperCase() + '_APPLICATION_INITIATED'
      : 'UNKNOWN_APPLICATION_INITIATED';
  }

  get ignore() {
    const route = this.url.pathname.split('/').pop();
    return this.meta.steps.map(r => r.route).includes(route!);
  }

  isSame = (path: string) => {
    return this.url.pathname === path;
  };

  get goto() {
    return {
      passport: this.meta.ask_for[Strings.ROUTES.PASSPORT]
        ? !this.passport_valid
        : false,
      visa: this.meta.ask_for[Strings.ROUTES.VISA] ? !this.visa_valid : false,
      financial: this.meta.ask_for[Strings.ROUTES.FINANCIAL]
        ? !this.financial_valid
        : false,
      additional: this.meta.ask_for[Strings.ROUTES.ADDITIONAL]
        ? !this.financial_valid
        : false,
    };
  }

  get user() {
    return {
      id: this.visa_request_id,
      email: this.email,
      username: this.full_name,
      country: this.meta.country,
      visitor_id: this.visitor_id,
    };
  }

  get prefix() {
    return `/${this.meta.subdomain}/visa_requests/${this.visa_request_id}/visitor/${this.visitor_id}/`;
  }

  redirect = (path: string) => {
    path = `${this.prefix}${path}`;
    if (!this.isSame(path)) {
      const params = new URL(this.urlRequest.url).searchParams;
      const newUrl = new URL(path, this.urlRequest.url);
      newUrl.search = params.toString();
      return routerRedirect(newUrl.toString());
    } else {
      return this.request;
    }
  };
}

export default RouteParser;
